import { parseLocalDate } from '@/utils/dates';
import { differenceInBusinessDays } from 'date-fns';

export type RiskStatus = 'on_track' | 'at_risk' | 'overdue' | 'unknown';

export interface CompletionTask {
    id: number;
    end_date: string | null;
}

/**
 * Find the latest task end date across all tasks.
 * Returns null when tasks is empty or all end_dates are invalid.
 */
export function calculateProjectCompletionDate(tasks: CompletionTask[]): Date | null {
    if (tasks.length === 0) return null;

    let latest: Date | null = null;
    for (const task of tasks) {
        if (!task.end_date) continue;
        const d = parseLocalDate(task.end_date);
        if (isNaN(d.getTime())) continue;
        if (latest === null || d > latest) latest = d;
    }
    return latest;
}

/**
 * Compare estimated completion against committed date.
 * Returns 'unknown' when estimatedCompletion is null (no valid tasks).
 */
export function calculateRiskStatus(
    estimatedCompletion: Date | null,
    committedDate: Date | null,
): RiskStatus {
    if (!estimatedCompletion || !committedDate) return 'unknown';
    return estimatedCompletion <= committedDate ? 'on_track' : 'at_risk';
}

/**
 * Working-day difference between estimated and committed dates.
 * Positive → late, negative → early.
 */
export function calculateDaysDifference(
    estimatedCompletion: Date,
    committedDate: Date,
): number {
    return differenceInBusinessDays(estimatedCompletion, committedDate);
}
